import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest, HttpStatusCode } from "@angular/common/http";
import { inject } from "@angular/core";

import { BehaviorSubject, catchError, filter, from, Observable, switchMap, take, throwError } from "rxjs";

import { AuthService } from "@core/services/auth.service";

// Track refresh state to avoid multiple simultaneous refresh calls
let isRefreshing = false;
// Queue of requests to retry after token refresh
const pendingRequests = new BehaviorSubject<boolean>(false);

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: (req: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);

  // Skip token handling for certain endpoints (but still attach auth for logout)
  if (req.url.includes("/auth/refresh") || req.url.includes("/auth/login")) {
    return next(req);
  }

  // Check if token needs refresh before sending the request
  if (authService.accessToken && shouldRefreshToken(authService.accessToken)) {
    return handleTokenRefresh(req, next, authService);
  }

  // Add auth header to request if token exists
  const token = authService.accessToken;
  if (token) {
    req = addAuthHeader(req, token);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === HttpStatusCode.Unauthorized) {
        // Avoid refresh loops when logged out or for auth/user or logout endpoints
        const isAuthUserEndpoint = req.url.includes("/auth/user");
        const isLogoutEndpoint = req.url.includes("/auth/logout");
        const hasToken = !!authService.accessToken;
        if (!hasToken || isAuthUserEndpoint || isLogoutEndpoint) {
          return throwError(() => error);
        }
        // Handle 401 errors with token refresh
        return handleTokenRefresh(req, next, authService);
      }
      return throwError(() => error);
    })
  );
};

/**
 * Handles token refresh and request retry logic
 */
function handleTokenRefresh(
  req: HttpRequest<unknown>,
  next: (req: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>,
  authService: AuthService
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    // Start refresh process
    isRefreshing = true;
    pendingRequests.next(false);

    return from(
      authService
        .refresh()
        .then(() => {
          pendingRequests.next(true);
          return authService.accessToken;
        })
        .catch(() => {
          // Handle refresh failure - clear session silently
          authService.accessToken = null;
          try {
            authService.currentUser.set(null);
          } catch {}
          return null;
        })
        .finally(() => {
          isRefreshing = false;
        })
    ).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(
            () =>
              new HttpErrorResponse({
                error: "Session expired",
                status: HttpStatusCode.Unauthorized,
                statusText: "Unauthorized"
              })
          );
        }
        // Retry the request with new token
        return next(addAuthHeader(req, token));
      })
    );
  } else {
    // Wait for the ongoing refresh to complete
    return pendingRequests.pipe(
      filter((completed) => completed),
      take(1),
      switchMap(() => {
        if (!authService.accessToken) {
          return throwError(
            () =>
              new HttpErrorResponse({
                error: "Session expired",
                status: HttpStatusCode.Unauthorized,
                statusText: "Unauthorized"
              })
          );
        }
        // Retry the request with new token
        return next(addAuthHeader(req, authService.accessToken));
      })
    );
  }
}

/**
 * Adds Authorization header with the provided token
 */
function addAuthHeader(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    headers: req.headers.set("Authorization", `Bearer ${token}`)
  });
}

/**
 * Checks if token needs to be refreshed based on expiration time
 */
function shouldRefreshToken(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expiresIn = payload.exp * 1000 - Date.now();
    // Refresh if token expires in less than 2 minutes
    return expiresIn < 120000;
  } catch {
    return false;
  }
}
